import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useUpdateMaintenanceWindowMutation } from "../api/maintenance.api";
import { maintenanceMessages } from "../maintenance.intl";
import { MaintenanceScheduleSchema } from "../schemas/MaintenanceSchema";
import {
  MaintenanceHistoryEntry,
  MaintenanceScheduleDraft,
} from "../types/maintenance.types";
import {
  parseMaintenanceWindowToDraft,
  buildCreateMaintenancePayload,
} from "../utils/maintenanceTime";

import MaintenanceForm from "./MaintenanceForm";
import MaintenanceReviewDialog from "./MaintenanceReviewDialog";

import { useIntlMessages } from "@/app/hooks/useIntlMessages";
import { customToast } from "@/components/alerts/CustomToast";
import FormDialog from "@/components/forms/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { validateResponse } from "@/utils/form/formHelper";

interface MaintenanceUpdateDialogProps {
  isOpen: boolean;
  maintenanceEntry?: MaintenanceHistoryEntry;
  onClose: () => void;
  onSuccess: () => void;
}

const MaintenanceUpdateDialog = (
  props: MaintenanceUpdateDialogProps,
): React.JSX.Element | null => {
  const { isOpen, maintenanceEntry, onClose, onSuccess } = props;
  const t = useIntlMessages(maintenanceMessages);
  const initialValues = useMemo(() => {
    return maintenanceEntry
      ? parseMaintenanceWindowToDraft(maintenanceEntry)
      : undefined;
  }, [maintenanceEntry]);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const form = useForm<MaintenanceScheduleDraft>({
    resolver: zodResolver(MaintenanceScheduleSchema),
    values: initialValues,
  });
  const [updateMaintenance, { isLoading }] =
    useUpdateMaintenanceWindowMutation();

  const handleExit = useCallback((): void => {
    setIsReviewOpen(false);
    onClose();
  }, [onClose]);

  const handleReview = useCallback((): void => {
    setIsReviewOpen(true);
  }, []);

  const handlePublish = useCallback(
    async (draft: MaintenanceScheduleDraft): Promise<void> => {
      if (!maintenanceEntry) {
        return;
      }

      const payload = buildCreateMaintenancePayload(draft);
      const response = await updateMaintenance({
        id: maintenanceEntry.id,
        data: payload,
      });

      validateResponse({
        response,
        onSuccess: () => {
          customToast.success(t("maintenanceUpdated"));
          setIsReviewOpen(false);
          onSuccess();
          onClose();
        },
      });
    },
    [maintenanceEntry, onClose, onSuccess, t, updateMaintenance],
  );

  if (!isOpen || !maintenanceEntry || !initialValues) {
    return null;
  }

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={handleExit}
      title={t("editMaintenance")}
      isFormDirty={form.formState.isDirty}
      className='sm:max-w-lg md:max-w-5xl'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleReview)}>
          <MaintenanceForm
            form={form}
            onReview={handleReview}
            onCancel={handleExit}
            hideActions
          />

          <div className='flex w-full justify-between my-2 pb-6'>
            <Button
              variant='ghost'
              className='font-normal hover:bg-transparent'
              type='button'
              onClick={handleExit}
            >
              {t("discardChanges")}
            </Button>
            <Button type='submit' isLoading={isLoading}>
              {t("review")}
            </Button>
          </div>
        </form>

        <MaintenanceReviewDialog
          open={isReviewOpen}
          onClose={() => setIsReviewOpen(false)}
          onPublish={handlePublish}
          isPublishing={isLoading}
        />
      </Form>
    </FormDialog>
  );
};

export { MaintenanceUpdateDialog };
